"use client";
import { Flex, Input, Box, Text, Button } from "@chakra-ui/react";
import { useGetAllModels, useUploadCheckpoint, useGetModel } from "queries";
import { useState } from "react";
import AddModel from "./AddModel";
import { fetch } from "libs";
import { DELETE_CHECKPOINT, UPDATE_MODEL_VISIBILITY } from "api"; // Import the API endpoint

export const UploadModel: React.FC = () => {
  const [pageSize] = useState(40);
  const tags: string[] = [];
  const { data } = useGetAllModels(false, pageSize, tags);
  const [modelId, setModelId] = useState("");
  const [file, setFile] = useState<File | undefined>();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [selectedModelName, setSelectedModelName] = useState("");
  const { data: selectedModel, refetch: refetchModel } = useGetModel(false, selectedModelName);

  const updateProgress = (progress: { loadedBytes: number }) => {
    const percent = file ? Math.round((progress.loadedBytes / file.size) * 100) : 0;
    setUploadProgress(percent);
  };

  const uploadCheckpoint = useUploadCheckpoint(updateProgress);

  const handleModelClick = (modelName: string) => {
    setSelectedModelName(modelName);
  };

  const handleDeleteCheckpoint = async (checkpointId: string) => {
    try {
      // Optimistically update the state
      if (selectedModel?.checkpoints) {
        const updatedCheckpoints = selectedModel.checkpoints.filter(
          (checkpoint) => checkpoint.id !== checkpointId
        );
        selectedModel.checkpoints = updatedCheckpoints;
        setSelectedModelName(selectedModel.username); // Trigger a re-render with updated state
      }

      const response = await fetch(DELETE_CHECKPOINT(checkpointId), {
        method: "DELETE",
      });

      if (response.ok) {
        refetchModel(); // Refetch model data to ensure the UI is in sync with the backend
      } else {
        console.error("Failed to delete the checkpoint. Response status:", response.status);
      }
    } catch (error) {
      console.error("Error occurred while deleting checkpoint:", error);
    }
  };

  const handleFileChange = (e: any) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    } else {
      setFile(undefined);
    }
  };

  const handleUpload = () => {
    if (file && selectedModel?.username) {
      uploadCheckpoint.mutate({ file, username: selectedModel?.username });
    }
  };

  // Function to directly call the API and update model visibility
  const handleToggleVisibility = async () => {
    if (selectedModel && selectedModel.visible !== undefined) {  // Ensure selectedModel has 'visible'
      const newVisibility = !selectedModel.visible; // Toggle visibility
      try {
        const url = `${UPDATE_MODEL_VISIBILITY}/${selectedModel.id}?visible=${newVisibility}`;
        const response = await fetch(url, {
          method: "PATCH",  // Ensure it's PATCH
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",  // Add Accept header
          },
        });

        if (response.ok) {
          refetchModel();
          window.location.replace(window.location.href);// Refetch model data to update the UI
        } else {
          console.error("Failed to update model visibility. Response status:", response.status);
          refetchModel();
          window.location.replace(window.location.href);
        }
      } catch (error) {
        console.error("Error occurred while updating model visibility:", error);
        refetchModel();
        window.location.replace(window.location.href);
      }
    }
  };

  return (
    <Flex>
      <Flex mr="50px" w="45%" direction="column">
        <Text fontWeight="semibold" fontSize="24px" mt="20px">
          Add Checkpoint to an existing model
        </Text>
        <Input h="50px" type="file" onChange={handleFileChange} />
        <Flex wrap="wrap" mt={4}>
          {data?.pages.map((page: any) =>
            page.models.map((model: any, index: number) => (
              <Box
                ml={index > 0 ? "5px" : ""}
                key={model.id}
                border={model.id === modelId ? "1px solid white" : "1px solid gray"}
                p={2}
                onClick={() => {
                  handleModelClick(model.username);
                  setModelId(model.id);
                }}
              >
                <Flex direction="column">
                  <Text color="white">{model.id}</Text>
                  <Text color="white">
                    {model.username} {model.visible ? "" : "(hidden)"} {/* Display "(hidden)" if the model is not visible */}
                  </Text>
                </Flex>
              </Box>
            ))
          )}{" "}
        </Flex>
        <Text color="white">Upload Progress: {uploadProgress}%</Text>{" "}
        <Button onClick={handleUpload}>Upload</Button>
        {selectedModel && (
          <Flex direction="column" mt={4}>
            <Text>Model: {selectedModel.username} {selectedModel.visible ? "" : "(hidden)"}</Text> {/* Display "(hidden)" if the model is not visible */}
            {/* Toggle visibility button */}
            <Button onClick={handleToggleVisibility}>
              {selectedModel.visible ? "Hide Model" : "Unhide Model"}
            </Button>
          </Flex>
        )}
        {selectedModel &&
          selectedModel.checkpoints &&
          selectedModel.checkpoints.map((checkpoint) => (
            <Flex key={checkpoint.id}>
              <Text>{checkpoint.file_name}</Text>
              <Button onClick={() => handleDeleteCheckpoint(checkpoint.id)}>Delete</Button>
            </Flex>
          ))}
      </Flex>

      <AddModel />
    </Flex>
  );
};

export default UploadModel;
